import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card, Button, Input, Select, Typography, Space, Tabs, Alert, message, Upload, Spin,
} from 'antd';
import { InboxOutlined, ThunderboltOutlined, SaveOutlined } from '@ant-design/icons';
import { api } from '@/services/api';
import { API_ENDPOINTS } from '@/constants';
import { CardPreviewTable } from '@/components/import/CardPreviewTable';
import type { PreviewCard } from '@/components/import/CardPreviewTable';
import type { ApiResponse, Subject, Chapter } from '@/types';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

interface PreviewResponse { previewToken: string; warnings: string[]; duplicateCards: number[]; cards: PreviewCard[] }
interface CommitResponse { deckId: string; cardCount: number }

function ImportForm({ onDone }: { onDone: () => void }) {
  const [mode, setMode] = useState<'paste' | 'file'>('paste');
  const [rawText, setRawText] = useState('');
  const [topicName, setTopicName] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState<string>();
  const [selectedChapterId, setSelectedChapterId] = useState<string>();
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>();
  const [selectedBoardId, setSelectedBoardId] = useState<string>();
  const [cards, setCards] = useState<PreviewCard[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [previewToken, setPreviewToken] = useState('');
  const [step, setStep] = useState<'input' | 'preview' | 'confirm'>('input');
  const [loading, setLoading] = useState(false);

  const { data: limitsData } = useQuery({
    queryKey: ['config', 'limits'],
    queryFn: () => api.get<never, ApiResponse<{ maxCardsPerDeck: number }>>('/admin/config/limits'),
    staleTime: 5 * 60 * 1000,
  });
  const maxCards: number = limitsData?.data?.maxCardsPerDeck ?? 10;

  const { data: boardsData } = useQuery({
    queryKey: ['curriculum', 'boards'],
    queryFn: () => api.get<never, ApiResponse<any[]>>(API_ENDPOINTS.CURRICULUM_BOARDS),
  });
  const { data: teachersData } = useQuery({
    queryKey: ['admin', 'teachers'],
    queryFn: () => api.get<never, ApiResponse<any[]>>('/admin/users?role=TEACHER&teacherStatus=ACTIVE&limit=200'),
  });
  const { data: chaptersData } = useQuery({
    queryKey: ['curriculum', 'chapters', selectedSubjectId],
    queryFn: () => api.get<never, ApiResponse<Chapter[]>>(`${API_ENDPOINTS.CURRICULUM_CHAPTERS}?subjectId=${selectedSubjectId}`),
    enabled: Boolean(selectedSubjectId),
  });

  const allBoards: any[] = boardsData?.data ?? [];
  const teachers: any[] = (teachersData as any)?.data?.data ?? [];

  // Filter boards/subjects to what the selected teacher teaches
  const selectedTeacher = teachers.find((t: any) => t.id === selectedTeacherId);
  const teacherSubjectIds: string[] = selectedTeacher?.subjectIds ?? [];
  const boards = teacherSubjectIds.length > 0
    ? allBoards
        .map((b: any) => ({ ...b, subjects: (b.subjects ?? []).filter((s: any) => teacherSubjectIds.includes(s.id)) }))
        .filter((b: any) => b.subjects.length > 0)
    : allBoards;
  const subjects: Subject[] = (selectedBoardId
    ? boards.find((b: any) => b.id === selectedBoardId)?.subjects ?? []
    : boards.flatMap((b: any) => b.subjects ?? [])
  );
  const chapters: Chapter[] = chaptersData?.data ?? [];

  const handleParse = async () => {
    if (!rawText.trim()) { message.error('Paste content first'); return; }
    setLoading(true);
    try {
      const res = await api.post<never, ApiResponse<{ cards: PreviewCard[] }>>(API_ENDPOINTS.IMPORT_PARSE, { rawText });
      setCards(res.data.cards);
      setStep('preview');
    } catch (e: any) {
      message.error(e?.error?.message ?? 'Parse failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!selectedTeacherId) { message.error('Select a teacher first'); return false; }
    // card count is unknown until the file is parsed on the server — limit enforced after upload response
    const formData = new FormData();
    formData.append('file', file);
    if (topicName) formData.append('topicName', topicName);
    if (selectedSubjectId) formData.append('subjectId', selectedSubjectId);
    if (selectedChapterId) formData.append('chapterId', selectedChapterId);
    formData.append('teacherId', selectedTeacherId);
    setLoading(true);
    try {
      const res = await api.post<never, ApiResponse<PreviewResponse>>(API_ENDPOINTS.IMPORT_UPLOAD, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCards(res.data.cards);
      setWarnings(res.data.warnings);
      setPreviewToken(res.data.previewToken);
      setStep('preview');
    } catch (e: any) {
      message.error(e?.error?.message ?? 'Upload failed');
    } finally {
      setLoading(false);
    }
    return false;
  };

  const handlePreview = async () => {
    if (!topicName.trim()) { message.error('Topic name required'); return; }
    if (!selectedTeacherId) { message.error('Select a teacher'); return; }
    if (cards.length > maxCards) { message.error(`Deck cannot have more than ${maxCards} cards`); return; }
    setLoading(true);
    try {
      const res = await api.post<never, ApiResponse<PreviewResponse>>(API_ENDPOINTS.IMPORT_PREVIEW, {
        topicName,
        subjectId: selectedSubjectId,
        chapterId: selectedChapterId,
        teacherId: selectedTeacherId,
        cards,
      });
      setWarnings(res.data.warnings);
      setPreviewToken(res.data.previewToken);
      const hasDeckDupe = res.data.warnings.some(w => w.startsWith('DUPLICATE_DECK'));
      if (hasDeckDupe) {
        message.error('Duplicate deck — rename topic');
      } else {
        setStep('confirm');
      }
    } catch (e: any) {
      message.error(e?.error?.message ?? 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    setLoading(true);
    try {
      const res = await api.post<never, ApiResponse<CommitResponse>>(API_ENDPOINTS.IMPORT_COMMIT, { previewToken });
      message.success(`Created deck with ${res.data.cardCount} cards`);
      onDone();
    } catch (e: any) {
      message.error(e?.error?.message ?? 'Commit failed');
    } finally {
      setLoading(false);
    }
  };

  const metaFields = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
      {/* Row 1: Teacher + Topic Name */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <Text strong>Teacher *</Text>
          <Select size="large" style={{ width: '100%', marginTop: 4 }} placeholder="Select teacher" value={selectedTeacherId} onChange={(v) => { setSelectedTeacherId(v); setSelectedBoardId(undefined); setSelectedSubjectId(undefined); setSelectedChapterId(undefined); }}
            options={teachers.map((t: any) => ({ value: t.id, label: t.name }))} showSearch
            filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())} />
        </div>
        <div>
          <Text strong>Topic Name *</Text>
          <Input size="large" style={{ marginTop: 4 }} value={topicName} onChange={e => setTopicName(e.target.value)} placeholder="e.g. Pythagoras Theorem" maxLength={100} />
        </div>
      </div>
      {/* Row 2: Board + Subject + Chapter */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <div>
          <Text strong>Board</Text>
          <Select style={{ width: '100%', marginTop: 4 }} placeholder="Select board" value={selectedBoardId}
            onChange={v => { setSelectedBoardId(v); setSelectedSubjectId(undefined); setSelectedChapterId(undefined); }}
            options={boards.map((b: any) => ({ value: b.id, label: b.name }))} allowClear />
        </div>
        <div>
          <Text strong>Subject</Text>
          <Select style={{ width: '100%', marginTop: 4 }} placeholder={selectedBoardId ? 'Select subject' : 'Select board first'}
            value={selectedSubjectId} disabled={!selectedBoardId}
            onChange={v => { setSelectedSubjectId(v); setSelectedChapterId(undefined); }}
            options={subjects.map(s => ({ value: s.id, label: s.name }))} allowClear />
        </div>
        <div>
          <Text strong>Chapter</Text>
          <Select style={{ width: '100%', marginTop: 4 }} placeholder={selectedSubjectId ? 'Select chapter' : 'Select subject first'}
            value={selectedChapterId} disabled={!selectedSubjectId} onChange={setSelectedChapterId}
            options={chapters.map(c => ({ value: c.id, label: c.name }))} allowClear />
        </div>
      </div>
    </div>
  );

  return (
    <Spin spinning={loading}>
      {step === 'input' && (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {metaFields}
          <Tabs activeKey={mode} onChange={k => setMode(k as 'paste' | 'file')} items={[
            {
              key: 'paste',
              label: 'Paste Text',
              children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <TextArea rows={12} value={rawText} onChange={e => setRawText(e.target.value)}
                    placeholder="Paste ChatGPT content here..." maxLength={20000} showCount />
                  <Button type="primary" icon={<ThunderboltOutlined />} onClick={handleParse} disabled={!rawText.trim()}>
                    Parse into Cards
                  </Button>
                </Space>
              ),
            },
            {
              key: 'file',
              label: 'Upload CSV / JSON',
              children: (
                <Dragger beforeUpload={handleFileUpload} accept=".csv,.json" maxCount={1} showUploadList={false}>
                  <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                  <p>Click or drag CSV/JSON file here</p>
                  <p style={{ color: '#888', fontSize: 13 }}>CSV columns: topic_name, card_title, front_text, back_text, sequence_order</p>
                </Dragger>
              ),
            },
          ]} />
        </Space>
      )}

      {step === 'preview' && (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {metaFields}
          {warnings.filter(w => w.startsWith('DUPLICATE_DECK')).map((w, i) => (
            <Alert key={i} type="error" message={w.replace('DUPLICATE_DECK: ', '')} />
          ))}
          <CardPreviewTable cards={cards} onChange={setCards} warnings={warnings} />
          {cards.length > maxCards && (
            <Alert
              type="error"
              message={`Too many cards: ${cards.length} / ${maxCards} max. Remove ${cards.length - maxCards} card(s) before validating.`}
              showIcon
            />
          )}
          <Space>
            <Button onClick={() => setStep('input')}>Back</Button>
            <Button
              type="primary"
              onClick={handlePreview}
              disabled={cards.length < 4 || cards.length > maxCards}
            >
              Validate ({cards.length} / {maxCards} cards)
            </Button>
          </Space>
        </Space>
      )}

      {step === 'confirm' && (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert type="success" message={`Ready: "${topicName}" — ${cards.length} cards`}
            description="Will be saved as DRAFT under the selected teacher." />
          {warnings.filter(w => !w.startsWith('DUPLICATE_DECK')).map((w, i) => (
            <Alert key={i} type="warning" message={w} />
          ))}
          <Space>
            <Button onClick={() => setStep('preview')}>Back</Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleCommit}>Confirm Import</Button>
          </Space>
        </Space>
      )}
    </Spin>
  );
}

export default function ImportBulkPage() {
  const [key, setKey] = useState(0);
  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px' }}>
      <Title level={3}>Bulk Import Content</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Import educational content as decks and cards on behalf of teachers. Existing data is never modified.
      </Text>
      <Card>
        <ImportForm onDone={() => setKey(k => k + 1)} key={key} />
      </Card>
    </div>
  );
}
